/**
 * The avatar query layer — Phase 2.5b. How a photo gets from the auth-checked
 * API route into an `<img src>` without breaking either contract.
 *
 * ===========================================================================
 * WHY THE URL THE `Avatar` COMPONENT RECEIVES IS AN OBJECT URL
 * ===========================================================================
 * `GET /users/:id/avatar` requires a Bearer token, and an image element cannot
 * send one. So the bytes travel through `fetchAvatarBlob` (the api client, with
 * the refresh mutex) and become a `blob:` object URL here. That indirection is
 * also what makes CACHE-BUSTING mechanical rather than clever:
 *
 *   upload/remove → `invalidateQueries(avatarKeys.user(id))`
 *                 → active queries refetch (`cache:'no-cache'` revalidates the
 *                   HTTP layer against the server's content-hash ETag)
 *                 → a NEW blob arrives → a NEW object URL → the `src` prop
 *                   CHANGES → React swaps the image.
 *
 * No timestamp query params, no `?v=` counters, no hard reload: the user sees
 * the new photo the moment the refetch lands, because a changed `src` is just a
 * changed prop. `profile-menu.test.tsx` asserts the refetch half; the ETag half
 * is the server's own e2e.
 *
 * ===========================================================================
 * 404 IS DATA, AND THE QUERY CACHES IT
 * ===========================================================================
 * `fetchAvatarBlob` answers `null` for "no photo", so a photo-less colleague is
 * a SUCCESSFUL query with `null` data — cached like any other answer, one
 * request per user per staleTime window rather than one per row render. An
 * actual failure (500, network) leaves `data` undefined and the caller falls
 * back to initials — the honest degraded state, indistinguishable on screen
 * from "no photo" because inventing a broken-image treatment for it would tell
 * the user nothing actionable.
 */

import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAvatarBlob, removeOwnAvatar, uploadOwnAvatar } from '../../lib/api';

export const avatarKeys = {
  /** One cache entry per user id — every render site of the same person shares it. */
  user: (id: string) => ['avatars', id] as const,
};

/**
 * A photo changes when somebody uploads one, and the uploader's own screen is
 * refreshed by invalidation, not by staleness. Five minutes bounds how long a
 * COLLEAGUE's replaced photo can stay stale on an already-open screen.
 */
const AVATAR_STALE_MS = 5 * 60_000;

/**
 * Blob → object URL, with the lifecycle React makes easy to leak: the URL is
 * derived per blob and REVOKED by an effect cleanup keyed on it, so a blob that
 * leaves the cache takes its committed URL with it (unmount included).
 *
 * `useMemo` rather than state-in-effect: the URL is pure derivation (same blob,
 * same URL), and the state shape trips `react-hooks/set-state-in-effect`. The
 * cost of the memo shape is confined to development — StrictMode double-invokes
 * memo callbacks, so dev strands one uncommitted URL per blob change; the
 * production build invokes once and every URL it makes is revoked.
 */
function useObjectUrl(blob: Blob | null): string | undefined {
  const url = useMemo(
    () => (blob === null ? undefined : URL.createObjectURL(blob)),
    [blob],
  );
  useEffect(() => {
    if (url === undefined) return undefined;
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [url]);
  return url;
}

/**
 * The `src` for one user's `Avatar`, or `undefined` while loading, for "no
 * photo", and on failure — all three of which the component already renders as
 * initials. Accepts whatever id shape the call site holds (`assigned_to` is a
 * string, `MeResponse.id` a number, absent for legacy rows).
 */
export function useAvatarUrl(userId: string | number | null | undefined): string | undefined {
  const id = userId === null || userId === undefined || userId === '' ? null : String(userId);
  const query = useQuery({
    // The '' key branch never fetches (`enabled` gates it); it exists because
    // hooks cannot be called conditionally and a key must always be present.
    queryKey: avatarKeys.user(id ?? ''),
    queryFn: () => (id === null ? Promise.resolve(null) : fetchAvatarBlob(id)),
    enabled: id !== null,
    staleTime: AVATAR_STALE_MS,
  });
  return useObjectUrl(query.data ?? null);
}

/**
 * Upload / remove for the signed-in user's own photo, each invalidating that
 * user's avatar query on success. The invalidation is AWAITED, so a caller
 * using `mutateAsync` resolves only after the refetch has landed — which is
 * what lets the profile menu drop its local preview at exactly the moment the
 * real photo is on screen, with no flash of the old one between.
 */
export function useOwnAvatarMutations(
  userId: string | number | null | undefined,
): {
  upload: ReturnType<typeof useMutation<Awaited<ReturnType<typeof uploadOwnAvatar>>, Error, File>>;
  remove: ReturnType<typeof useMutation<Awaited<ReturnType<typeof removeOwnAvatar>>, Error, void>>;
} {
  const queryClient = useQueryClient();
  const id = userId === null || userId === undefined || userId === '' ? null : String(userId);

  const settle = async (): Promise<void> => {
    if (id !== null) {
      await queryClient.invalidateQueries({ queryKey: avatarKeys.user(id) });
    }
  };

  const upload = useMutation({
    mutationFn: (file: File) => uploadOwnAvatar(file),
    onSuccess: settle,
  });
  const remove = useMutation({
    mutationFn: () => removeOwnAvatar(),
    onSuccess: settle,
  });

  return { upload, remove };
}
